/**
 * Centralized logging utility for Latio Admin Dashboard
 * Provides consistent logging across the application with configurable levels
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

export enum LogCategory {
  AUTH = 'AUTH',
  API = 'API',
  TOKEN = 'TOKEN',
  UI = 'UI',
  NAVIGATION = 'NAV',
  WEBSOCKET = 'WS',
  ERROR = 'ERROR',
  PERFORMANCE = 'PERF',
  SECURITY = 'SEC'
}

class Logger {
  private static instance: Logger;
  private static readonly STORAGE_KEY = 'latio_admin_log_config';
  private logLevel: LogLevel = LogLevel.INFO;
  private enabledCategories: Set<LogCategory> = new Set(Object.values(LogCategory));
  private isDevelopment: boolean = process.env.NODE_ENV === 'development';
  private listeners: Set<() => void> = new Set();

  private constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const saved = localStorage.getItem(Logger.STORAGE_KEY);
      if (saved) {
        const config = JSON.parse(saved);
        this.logLevel = config.logLevel ?? (this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN);
        this.enabledCategories = new Set(config.enabledCategories ?? Object.values(LogCategory));
        return;
      }
    } catch {
      // Fall through to defaults
    }
    // Defaults
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
    this.enabledCategories = new Set(Object.values(LogCategory));
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(Logger.STORAGE_KEY, JSON.stringify({
        logLevel: this.logLevel,
        enabledCategories: Array.from(this.enabledCategories),
      }));
    } catch {
      // Ignore storage errors
    }
    this.notifyListeners();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(fn => fn());
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    return level <= this.logLevel && this.enabledCategories.has(category);
  }

  private formatMessage(level: LogLevel, category: LogCategory, message: string, component?: string): string {
    const levelIcons = {
      [LogLevel.ERROR]: '🚨',
      [LogLevel.WARN]: '⚠️',
      [LogLevel.INFO]: 'ℹ️',
      [LogLevel.DEBUG]: '🔧'
    };

    const componentStr = component ? `[${component}]` : '';
    return `${levelIcons[level]} ${category}${componentStr}: ${message}`;
  }

  private log(level: LogLevel, category: LogCategory, message: string, data?: any, component?: string): void {
    if (!this.shouldLog(level, category)) return;

    const formattedMessage = this.formatMessage(level, category, message, component);

    switch (level) {
      case LogLevel.ERROR:
        if (data) {
          console.error(formattedMessage, data);
        } else {
          console.error(formattedMessage);
        }
        break;
      case LogLevel.WARN:
        if (data) {
          console.warn(formattedMessage, data);
        } else {
          console.warn(formattedMessage);
        }
        break;
      case LogLevel.INFO:
        if (data) {
          console.log(formattedMessage, data);
        } else {
          console.log(formattedMessage);
        }
        break;
      case LogLevel.DEBUG:
        if (data) {
          console.log(formattedMessage, data);
        } else {
          console.log(formattedMessage);
        }
        break;
    }
  }

  // Public logging methods
  error(category: LogCategory, message: string, data?: any, component?: string): void {
    this.log(LogLevel.ERROR, category, message, data, component);
  }

  warn(category: LogCategory, message: string, data?: any, component?: string): void {
    this.log(LogLevel.WARN, category, message, data, component);
  }

  info(category: LogCategory, message: string, data?: any, component?: string): void {
    this.log(LogLevel.INFO, category, message, data, component);
  }

  debug(category: LogCategory, message: string, data?: any, component?: string): void {
    this.log(LogLevel.DEBUG, category, message, data, component);
  }

  // Token-specific logging methods
  tokenDebug(message: string, data?: any, component?: string): void {
    this.debug(LogCategory.TOKEN, message, data, component);
  }

  tokenInfo(message: string, data?: any, component?: string): void {
    this.info(LogCategory.TOKEN, message, data, component);
  }

  tokenWarn(message: string, data?: any, component?: string): void {
    this.warn(LogCategory.TOKEN, message, data, component);
  }

  tokenError(message: string, data?: any, component?: string): void {
    this.error(LogCategory.TOKEN, message, data, component);
  }

  // API-specific logging methods
  apiDebug(message: string, data?: any, component?: string): void {
    this.debug(LogCategory.API, message, data, component);
  }

  apiInfo(message: string, data?: any, component?: string): void {
    this.info(LogCategory.API, message, data, component);
  }

  apiWarn(message: string, data?: any, component?: string): void {
    this.warn(LogCategory.API, message, data, component);
  }

  apiError(message: string, data?: any, component?: string): void {
    this.error(LogCategory.API, message, data, component);
  }

  // Auth-specific logging methods
  authDebug(message: string, data?: any, component?: string): void {
    this.debug(LogCategory.AUTH, message, data, component);
  }

  authInfo(message: string, data?: any, component?: string): void {
    this.info(LogCategory.AUTH, message, data, component);
  }

  authWarn(message: string, data?: any, component?: string): void {
    this.warn(LogCategory.AUTH, message, data, component);
  }

  authError(message: string, data?: any, component?: string): void {
    this.error(LogCategory.AUTH, message, data, component);
  }

  // UI-specific logging methods
  uiDebug(message: string, data?: any, component?: string): void {
    this.debug(LogCategory.UI, message, data, component);
  }

  uiInfo(message: string, data?: any, component?: string): void {
    this.info(LogCategory.UI, message, data, component);
  }

  uiWarn(message: string, data?: any, component?: string): void {
    this.warn(LogCategory.UI, message, data, component);
  }

  uiError(message: string, data?: any, component?: string): void {
    this.error(LogCategory.UI, message, data, component);
  }

  // Configuration methods
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.saveToStorage();
  }

  enableCategory(category: LogCategory): void {
    this.enabledCategories.add(category);
    this.saveToStorage();
  }

  disableCategory(category: LogCategory): void {
    this.enabledCategories.delete(category);
    this.saveToStorage();
  }

  toggleCategory(category: LogCategory): void {
    if (this.enabledCategories.has(category)) {
      this.enabledCategories.delete(category);
    } else {
      this.enabledCategories.add(category);
    }
    this.saveToStorage();
  }

  enableAllCategories(): void {
    Object.values(LogCategory).forEach(cat => this.enabledCategories.add(cat));
    this.saveToStorage();
  }

  disableAllCategories(): void {
    this.enabledCategories.clear();
    this.saveToStorage();
  }

  resetToDefaults(): void {
    localStorage.removeItem(Logger.STORAGE_KEY);
    this.loadFromStorage();
    this.notifyListeners();
  }

  // Utility methods for development
  getCurrentConfig(): { logLevel: LogLevel; enabledCategories: LogCategory[] } {
    return {
      logLevel: this.logLevel,
      enabledCategories: Array.from(this.enabledCategories)
    };
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export convenience functions
export const {
  error,
  warn,
  info,
  debug,
  tokenDebug,
  tokenInfo,
  tokenWarn,
  tokenError,
  apiDebug,
  apiInfo,
  apiWarn,
  apiError,
  authDebug,
  authInfo,
  authWarn,
  authError,
  uiDebug,
  uiInfo,
  uiWarn,
  uiError
} = logger;